import React, { useMemo } from 'react';
import {
  Lightbulb, Target, MousePointerClick, Users, ShoppingBag, Smartphone, TrendingUp,
  DollarSign, Clock, AlertTriangle, ImageIcon, Megaphone, Layers, CheckCircle2,
  Info, Sparkles,
} from 'lucide-react';
import { useFbWorkspace } from '@/contexts/FbWorkspaceContext';

// ─── Per-objective playbook ───────────────────────────────────────────────────

interface ObjectivePlaybook {
  icon: React.ReactNode;
  label: string;
  blurb: string;
  bidStrategies: string[];
  dailyBudgetMin: number; // AED
  dailyBudgetTypical: string; // AED range, human-readable
  audience: string;
  creativeTips: string[];
}

const OBJECTIVE_PLAYBOOKS: Record<string, ObjectivePlaybook> = {
  OUTCOME_AWARENESS: {
    icon: <TrendingUp size={16} />,
    label: 'Awareness',
    blurb: 'Optimised for reach & ad recall lift. Good for new product launches and brand campaigns.',
    bidStrategies: ['Lowest cost (default)', 'Bid cap if you have a known CPM target'],
    dailyBudgetMin: 30,
    dailyBudgetTypical: 'AED 50–150 / day',
    audience: 'Broad audiences (500k+) work best — let Meta find the brand-recall pockets.',
    creativeTips: [
      'Lead with the brand in the first 2 seconds of video.',
      'Use 9:16 vertical for Reels/Stories, 1:1 for feed.',
      'Keep messaging single-focus — one logo, one promise.',
    ],
  },
  OUTCOME_TRAFFIC: {
    icon: <MousePointerClick size={16} />,
    label: 'Traffic',
    blurb: 'Drives clicks to a destination (site, app, WhatsApp). Best when you want pageviews, not just impressions.',
    bidStrategies: ['Lowest cost', 'Cost cap if your landing page converts at a known rate'],
    dailyBudgetMin: 20,
    dailyBudgetTypical: 'AED 30–100 / day',
    audience: 'Combine interest + lookalike (1–3%). Exclude past 30-day site visitors.',
    creativeTips: [
      'Strong CTA in the first frame ("Shop now", "Learn more").',
      'Show the destination upfront — a teaser kills CTR.',
      'Carousel ads work well for category pages.',
    ],
  },
  OUTCOME_ENGAGEMENT: {
    icon: <Users size={16} />,
    label: 'Engagement',
    blurb: 'Boost messages, post reactions, video views, or page follows. Great for community building.',
    bidStrategies: ['Lowest cost', 'Bid cap for messages → keep CPM in check'],
    dailyBudgetMin: 15,
    dailyBudgetTypical: 'AED 20–80 / day',
    audience: 'Warm audiences (page fans, video viewers, IG engagers) deliver 2–3× the rate.',
    creativeTips: [
      'Ask a question or use a poll-style sticker.',
      'For messages → make the CTA "Send Message" and prep auto-replies.',
      'UGC-style creative outperforms polished ads here.',
    ],
  },
  OUTCOME_LEADS: {
    icon: <Target size={16} />,
    label: 'Leads',
    blurb: 'Collects contact info via Instant Forms, Messenger, or your site. Most direct ROI lever for B2B & high-ticket.',
    bidStrategies: ['Lowest cost', 'Cost cap to defend CPL', 'Highest volume for top of funnel'],
    dailyBudgetMin: 50,
    dailyBudgetTypical: 'AED 80–250 / day',
    audience: '1% lookalikes off your customer list usually beat broad targeting by 30–60%.',
    creativeTips: [
      'Pre-qualify in the ad copy ("UAE residents only", "12+ employees") — fewer junk leads.',
      'Use the Higher Intent form template — adds a review screen, lifts quality.',
      'Show the offer (consultation, quote, demo) in the headline, not just creative.',
    ],
  },
  OUTCOME_SALES: {
    icon: <ShoppingBag size={16} />,
    label: 'Sales',
    blurb: 'Optimises for purchases via the pixel/SDK. Needs a working catalog and conversions API for best results.',
    bidStrategies: ['Lowest cost (early phase)', 'Cost cap once you have ROAS data', 'Minimum ROAS for mature accounts'],
    dailyBudgetMin: 80,
    dailyBudgetTypical: 'AED 150–500 / day',
    audience: 'Advantage+ Shopping Audience usually outperforms manual targeting once pixel is mature.',
    creativeTips: [
      'Show price + product clearly. "From AED 99" beats "Affordable luxury".',
      'Dynamic product ads with catalog feed are baseline — not optional.',
      'Test 3–5 creative variants per ad set, kill the bottom 2 by day 5.',
    ],
  },
  OUTCOME_APP_PROMOTION: {
    icon: <Smartphone size={16} />,
    label: 'App Promotion',
    blurb: 'Optimised for installs and in-app events. Requires Facebook SDK or MMP (Adjust, AppsFlyer).',
    bidStrategies: ['Lowest cost', 'Cost cap on CPI', 'Value optimisation if you track revenue events'],
    dailyBudgetMin: 100,
    dailyBudgetTypical: 'AED 200–600 / day',
    audience: 'Advantage+ App Campaigns simplifies setup — let Meta optimise placements & targeting.',
    creativeTips: [
      'Show actual app UI in the first 3s of video.',
      'Use 9:16 vertical — 80%+ of installs come from Reels/Stories now.',
      'Test "store badge" CTA vs "Install Now" — varies by category.',
    ],
  },
};

// ─── Tips bucket (small reusable list) ────────────────────────────────────────

function TipList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-1.5">
      {items.map((t, i) => (
        <li key={i} className="flex gap-2 text-xs text-text-secondary leading-relaxed">
          <CheckCircle2 size={12} className="mt-0.5 shrink-0 text-success" />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

function SectionHeader({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <span className="text-primary">{icon}</span>
      <h4 className="text-xs font-semibold uppercase tracking-wide text-text-primary">{title}</h4>
    </div>
  );
}

function HelpCard({ children, tone = 'default' }: { children: React.ReactNode; tone?: 'default' | 'warning' | 'info' | 'success' }) {
  const toneClasses = {
    default: 'border-border bg-surface',
    warning: 'border-warning/30 bg-warning-soft/30',
    info: 'border-info/30 bg-info-soft/30',
    success: 'border-success/30 bg-success-soft/30',
  } as const;
  return <div className={`rounded-xl border p-4 ${toneClasses[tone]}`}>{children}</div>;
}

// ─── Campaign-level help ──────────────────────────────────────────────────────

function CampaignHelp() {
  const { campaign } = useFbWorkspace();
  const fd = campaign.formData as Record<string, string>;
  const objective = (fd.objective ?? '') as string;
  const budgetAt = (fd.budget_at ?? 'campaign') as 'campaign' | 'adset';
  const dailyBudget = parseFloat(fd.daily_budget ?? '');
  const lifetimeBudget = parseFloat(fd.lifetime_budget ?? '');
  const specialCats = (fd.special_ad_categories ? JSON.parse(fd.special_ad_categories) : []) as string[];

  const playbook = objective ? OBJECTIVE_PLAYBOOKS[objective] : null;

  // Budget math
  const budgetInsight = useMemo(() => {
    if (budgetAt !== 'campaign') return null;
    if (dailyBudget && !isNaN(dailyBudget) && dailyBudget > 0) {
      const monthly = (dailyBudget * 30).toFixed(0);
      const learning = (dailyBudget * 7).toFixed(0);
      const minRec = playbook?.dailyBudgetMin ?? 20;
      const tooLow = dailyBudget < minRec;
      return {
        kind: 'daily' as const,
        monthly,
        learning,
        tooLow,
        minRec,
      };
    }
    if (lifetimeBudget && !isNaN(lifetimeBudget) && lifetimeBudget > 0) {
      return {
        kind: 'lifetime' as const,
        weekly: (lifetimeBudget / 4).toFixed(0),
      };
    }
    return null;
  }, [budgetAt, dailyBudget, lifetimeBudget, playbook]);

  // Empty state — nothing chosen yet
  if (!objective) {
    return (
      <HelpCard tone="info">
        <div className="flex gap-3">
          <Lightbulb size={18} className="shrink-0 text-info mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-text-primary mb-1">Pick an objective first</h4>
            <p className="text-xs text-text-secondary leading-relaxed">
              Your objective drives every downstream optimisation — bid strategy, audience signals, even which placements
              run. Choose what matches the action you want people to take, not what sounds biggest.
            </p>
            <div className="mt-3 grid grid-cols-2 gap-1.5 text-2xs text-text-muted">
              <div className="flex items-center gap-1.5"><Target size={11} /> Leads → form fills</div>
              <div className="flex items-center gap-1.5"><ShoppingBag size={11} /> Sales → purchases</div>
              <div className="flex items-center gap-1.5"><MousePointerClick size={11} /> Traffic → site clicks</div>
              <div className="flex items-center gap-1.5"><Users size={11} /> Engagement → messages</div>
            </div>
          </div>
        </div>
      </HelpCard>
    );
  }

  return (
    <div className="space-y-3">
      {/* Objective deep-dive */}
      {playbook && (
        <HelpCard>
          <SectionHeader icon={playbook.icon} title={`${playbook.label} objective`} />
          <p className="text-xs text-text-secondary leading-relaxed mb-3">{playbook.blurb}</p>

          <div className="space-y-3">
            <div>
              <div className="flex items-center gap-1.5 mb-1">
                <DollarSign size={11} className="text-text-muted" />
                <span className="text-2xs uppercase tracking-wide text-text-muted">Typical spend</span>
              </div>
              <p className="text-sm font-semibold text-text-primary">{playbook.dailyBudgetTypical}</p>
              <p className="text-2xs text-text-muted mt-0.5">Min recommended: AED {playbook.dailyBudgetMin}/day</p>
            </div>

            <div>
              <div className="flex items-center gap-1.5 mb-1">
                <Sparkles size={11} className="text-text-muted" />
                <span className="text-2xs uppercase tracking-wide text-text-muted">Audience signal</span>
              </div>
              <p className="text-xs text-text-secondary leading-relaxed">{playbook.audience}</p>
            </div>

            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <ImageIcon size={11} className="text-text-muted" />
                <span className="text-2xs uppercase tracking-wide text-text-muted">Creative tips</span>
              </div>
              <TipList items={playbook.creativeTips} />
            </div>
          </div>
        </HelpCard>
      )}

      {/* Bid strategy cheat sheet */}
      {playbook && (
        <HelpCard>
          <SectionHeader icon={<TrendingUp size={14} />} title="Recommended bid strategies" />
          <ul className="space-y-1.5">
            {playbook.bidStrategies.map((b, i) => (
              <li key={i} className="flex gap-2 text-xs text-text-secondary">
                <span className="text-primary font-semibold">{i + 1}.</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-2xs text-text-muted leading-relaxed">
            Start with <strong className="text-text-secondary">Lowest cost</strong> for the first 7 days while Meta
            collects learning data. Switch to a cap once you have a benchmark CPL/CPA.
          </p>
        </HelpCard>
      )}

      {/* Budget breakdown */}
      {budgetInsight && (
        <HelpCard tone={budgetInsight.kind === 'daily' && budgetInsight.tooLow ? 'warning' : 'success'}>
          <SectionHeader icon={<DollarSign size={14} />} title="Budget breakdown" />
          {budgetInsight.kind === 'daily' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-2xs uppercase tracking-wide text-text-muted">Per week</div>
                  <div className="text-sm font-semibold text-text-primary tabular-nums">AED {budgetInsight.learning}</div>
                </div>
                <div>
                  <div className="text-2xs uppercase tracking-wide text-text-muted">Per month</div>
                  <div className="text-sm font-semibold text-text-primary tabular-nums">AED {budgetInsight.monthly}</div>
                </div>
              </div>
              {budgetInsight.tooLow && (
                <p className="mt-3 flex gap-2 text-2xs text-warning leading-relaxed">
                  <AlertTriangle size={11} className="shrink-0 mt-0.5" />
                  Below the AED {budgetInsight.minRec}/day floor for this objective. Meta&apos;s learning phase needs
                  ~50 conversions per week — too little budget can keep ad sets stuck in &ldquo;Learning&rdquo;.
                </p>
              )}
            </>
          )}
          {budgetInsight.kind === 'lifetime' && (
            <div className="text-xs text-text-secondary">
              Lifetime budget paces over the campaign window. Roughly{' '}
              <strong className="text-text-primary tabular-nums">AED {budgetInsight.weekly}/week</strong> at a 4-week cadence.
            </div>
          )}
        </HelpCard>
      )}

      {/* CBO vs ABO explainer */}
      <HelpCard tone="info">
        <SectionHeader icon={<Info size={14} />} title={budgetAt === 'campaign' ? 'CBO selected' : 'ABO selected'} />
        {budgetAt === 'campaign' ? (
          <p className="text-xs text-text-secondary leading-relaxed">
            <strong className="text-text-primary">Campaign Budget Optimisation</strong> lets Meta shift spend across
            ad sets in real time toward the best performer. Best when ad sets target similar funnels.
          </p>
        ) : (
          <p className="text-xs text-text-secondary leading-relaxed">
            <strong className="text-text-primary">Ad Set Budget Optimisation</strong> gives each ad set its own
            budget. Use when you need guaranteed spend per audience (e.g. retargeting vs prospecting).
          </p>
        )}
      </HelpCard>

      {/* Special categories warning */}
      {specialCats.length > 0 && (
        <HelpCard tone="warning">
          <SectionHeader icon={<AlertTriangle size={14} />} title="Special ad category restrictions" />
          <p className="text-xs text-text-secondary leading-relaxed">
            You&apos;ve flagged this campaign as {specialCats.join(', ')}. Meta will restrict targeting:
          </p>
          <ul className="mt-2 space-y-1 text-2xs text-text-muted">
            <li>• No detailed targeting on age, gender, ZIP, or interests</li>
            <li>• Lookalike audiences are limited or disabled</li>
            <li>• Creative is held to stricter review (24–48h delay common)</li>
          </ul>
        </HelpCard>
      )}
    </div>
  );
}

// ─── Ad Set-level help ────────────────────────────────────────────────────────

function AdSetHelp() {
  const { campaign, activeAdSet } = useFbWorkspace();
  const playbook = OBJECTIVE_PLAYBOOKS[campaign.objective];

  return (
    <div className="space-y-3">
      <HelpCard tone="info">
        <SectionHeader icon={<Layers size={14} />} title="Ad set targeting" />
        <p className="text-xs text-text-secondary leading-relaxed">
          The ad set is where you set <strong>who</strong> sees the ad. Keep one variable per ad set —
          changing too many things at once makes results uncomparable.
        </p>
      </HelpCard>

      {playbook && (
        <HelpCard>
          <SectionHeader icon={<Target size={14} />} title={`${playbook.label} audience tips`} />
          <p className="text-xs text-text-secondary leading-relaxed mb-3">{playbook.audience}</p>
          <ul className="space-y-1.5 text-xs text-text-secondary">
            <li className="flex gap-2"><CheckCircle2 size={12} className="mt-0.5 shrink-0 text-success" />Audience size 500k–2M is the sweet spot for most objectives.</li>
            <li className="flex gap-2"><CheckCircle2 size={12} className="mt-0.5 shrink-0 text-success" />Avoid stacking 5+ interests — narrows reach without precision gain.</li>
            <li className="flex gap-2"><CheckCircle2 size={12} className="mt-0.5 shrink-0 text-success" />Always exclude existing customers on prospecting ad sets.</li>
          </ul>
        </HelpCard>
      )}

      <HelpCard tone="info">
        <SectionHeader icon={<Clock size={14} />} title="Schedule & pacing" />
        <ul className="space-y-1.5 text-xs text-text-secondary">
          <li className="flex gap-2"><CheckCircle2 size={12} className="mt-0.5 shrink-0 text-success" />Run for 7+ days before judging — Meta&apos;s learning phase needs the volume.</li>
          <li className="flex gap-2"><CheckCircle2 size={12} className="mt-0.5 shrink-0 text-success" />Standard pacing distributes evenly; accelerated burns budget early.</li>
          <li className="flex gap-2"><CheckCircle2 size={12} className="mt-0.5 shrink-0 text-success" />Dayparting only helps if you have clear off-hour data showing waste.</li>
        </ul>
      </HelpCard>

      {activeAdSet && activeAdSet.ads.length === 0 && (
        <HelpCard tone="warning">
          <div className="flex gap-2">
            <AlertTriangle size={14} className="shrink-0 mt-0.5 text-warning" />
            <p className="text-xs text-text-secondary leading-relaxed">
              This ad set has no ads yet. Add 3–5 creative variants so Meta has variants to test.
            </p>
          </div>
        </HelpCard>
      )}
    </div>
  );
}

// ─── Ad-level help ────────────────────────────────────────────────────────────

function AdHelp() {
  const { campaign } = useFbWorkspace();
  const playbook = OBJECTIVE_PLAYBOOKS[campaign.objective];

  return (
    <div className="space-y-3">
      <HelpCard tone="info">
        <SectionHeader icon={<ImageIcon size={14} />} title="Creative best practices" />
        {playbook ? <TipList items={playbook.creativeTips} /> : (
          <p className="text-xs text-text-secondary">Pick an objective on the campaign tab — creative recommendations depend on it.</p>
        )}
      </HelpCard>

      <HelpCard>
        <SectionHeader icon={<Megaphone size={14} />} title="Copy length cheat-sheet" />
        <div className="space-y-2 text-xs">
          <div className="flex justify-between">
            <span className="text-text-muted">Primary text</span>
            <span className="font-semibold text-text-primary tabular-nums">125 chars</span>
          </div>
          <div className="flex justify-between">
            <span className="text-text-muted">Headline</span>
            <span className="font-semibold text-text-primary tabular-nums">40 chars</span>
          </div>
          <div className="flex justify-between">
            <span className="text-text-muted">Description</span>
            <span className="font-semibold text-text-primary tabular-nums">30 chars</span>
          </div>
        </div>
        <p className="mt-3 text-2xs text-text-muted leading-relaxed">
          Anything longer gets truncated on mobile feeds. Front-load the offer.
        </p>
      </HelpCard>

      <HelpCard>
        <SectionHeader icon={<Sparkles size={14} />} title="Format ranking (2026)" />
        <ol className="space-y-1.5 text-xs text-text-secondary">
          <li><strong className="text-text-primary">1. Reels (9:16 video)</strong> — highest reach, lowest CPM right now.</li>
          <li><strong className="text-text-primary">2. Carousel</strong> — best CTR for product pages.</li>
          <li><strong className="text-text-primary">3. Single image (1:1)</strong> — fast to ship, good A/B baseline.</li>
          <li><strong className="text-text-primary">4. Collection</strong> — only if you have a catalog feed.</li>
        </ol>
      </HelpCard>
    </div>
  );
}

// ─── Main panel ───────────────────────────────────────────────────────────────

export const ContextHelpPanel: React.FC = () => {
  const { activeLevel } = useFbWorkspace();

  return (
    // 1800px, not xl. The workspace already spends 560px on the app nav rail,
    // the campaign tree and padding, and the ad panel spends another 344 on the
    // creative preview — so at 1280 this panel left the ad form under 300px,
    // which is what read as "the panels collide". It only earns its space once
    // the form still clears ~570px without it.
    <aside className="hidden min-[1800px]:block w-[320px] shrink-0 border-l border-border bg-surface-alt/30 overflow-y-auto">
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-surface-alt/80 backdrop-blur px-4 py-3">
        <Lightbulb size={14} className="text-primary" />
        <h3 className="text-xs font-semibold uppercase tracking-wide text-text-primary">Context & tips</h3>
      </div>
      <div className="p-4">
        {activeLevel === 'campaign' && <CampaignHelp />}
        {activeLevel === 'adset'    && <AdSetHelp />}
        {activeLevel === 'ad'       && <AdHelp />}
      </div>
    </aside>
  );
};
